import React, { useCallback, useContext, useMemo, useState, useEffect } from 'react';

import { Form } from 'react-final-form';
import { Link, useNavigate, useLocation } from 'react-router-dom';

import styled from 'styled-components';

import verificationBGImage from '../../assets/images/amazon/verificationBGImage.png';
import errorIcon from '../../assets/images/error.png';
import warningIcon from '../../assets/images/warning.png';
import TenantConfig, { getTenant } from '../../config';
import { UserContext } from '../../context/UserContext';
import { composeValidators, email, max, required } from '../../helpers/validation';
import rudderanalytics from '../../service/rudderstack';
import BnplFormContainer from '../Bnpl/BnplFormContainer';
import BnplLoading from '../Bnpl/BnplLoading';
import BnplSignIn from '../Bnpl/BnplSignIn';
import FormInput from '../FormElements/FormInput';
import MerchantPortalFooter from '../common/MerchantPortalFooter';

const WarningBox = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
  color: #f36a3f;
  font-weight: 600;
  flex-wrap: wrap;
  a {
    color: #f36a3f;
    text-decoration: underline;

    &:hover {
      color: initial;
    }
  }
`;

const WarningSection = styled.div`
  padding: 22px 36px;
  background: #feece7;
  margin-top: -40px;
  margin-bottom: 60px;

  @media screen and (max-width: 600px) {
    padding: 20px;
    width: calc(100% - 40px);
    margin-bottom: 0;
  }

  &.auth-warningsection {
    width: inherit;
    margin-top: 0px;
    margin-bottom: 10px;
  }
`;

const WarningContent = styled.span`
  color: #f15422;
  font-size: 16px;
  line-height: 24px;

  & > a {
    color: #f36a3f;

    &:hover {
      color: initial;
    }
  }
`;

const SignIn = ({ onSubmit, className }) => {
  const { loading, setLoading, width } = useContext(UserContext);
  const [errCode, setErrCode] = useState('');
  const [errMsg, setErrMsg] = useState('');
  const [pendingAttempts, setPendingAttempts] = useState(0);
  const navigate = useNavigate();
  const location = useLocation();

  const formOptions = useMemo(() => {
    return {
      fields: [
        {
          label: 'Email',
          placeholder: 'Please enter your email address',
          required: true,
          type: 'email',
          validate: composeValidators(
            max('Maximum length is 50 characters', 50),
            required("Email can't be empty"),
            email('Email is invalid')
          ),
          name: 'email',
        },
        {
          label: 'Password',
          placeholder: 'Enter your password',
          required: true,
          type: 'password',
          validate: composeValidators(
            max('Maximum length is 64 characters', 64),
            required("Password can't be empty")
          ),
          name: 'password',
        },
      ],
    };
  }, []);

  const onSubmitInternal = useCallback(
    async (values) => {
      setLoading(true);
      try {
        const res = await onSubmit(values);
        setLoading(false);
        if (res?.data?.actionRequired == 'SELECT_OTP_CHANNEL') {
          navigate('/auth/select-channel' + location.search);
        } else {
          navigate('/auth/phone-verification' + location.search);
        }
      } catch (err) {
        if (err?.response?.data?.errors && err.response.data.errors.length > 0) {
          const error_code = err.response.data.errors[0].code;
          setErrCode(error_code);
          rudderanalytics.page('SignIn');
          if (error_code === 'USER_IS_DISABLED') {
            setErrMsg('This account is locked.');
            rudderanalytics.identify(values.email, {
              message: 'Account locked',
            });
          } else if (error_code === 'USER_NOT_PRESENT_OR_BAD_CREDENTIALS') {
            setErrMsg('Incorrect password.');
            let attempsRemaining = err.response.data.errors[0].info.pendingAttempts;
            setPendingAttempts(attempsRemaining);
            rudderanalytics.identify(values.email, {
              message: 'Login failed',
              pendingAttempts: err.response.data.errors[0].info.pendingAttempts,
            });
          } else if (error_code === 'ENTITY_NOT_FOUND') {
            setLoading(false);
            return { email: 'Email not found!' };
          } else {
            setErrMsg('Please check your email and password.');
            if (typeof newrelic !== 'undefined') {
              newrelic.noticeError(err);
            }
          }
        } else {
          if (typeof newrelic !== 'undefined') {
            newrelic.noticeError(err);
          }
        }
        setLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [navigate, location, onSubmit]
  );

  const scrollToBottom = () => {
    window.scrollTo(0, document.body.scrollHeight);
  };

  const resetErrors = () => {
    setErrCode(null);
    setErrMsg(null);
  };

  if (getTenant() === 'lightrx' && className !== 'admin') {
    return (
      <BnplFormContainer showControls={false}>
        {loading && <BnplLoading className={'bnpl-form'} isAuth />}
        <BnplSignIn className="bnpl-form" onSubmitInternal={onSubmitInternal} />
      </BnplFormContainer>
    );
  }

  const badCredentialsWarningBox = () => {
    return (
      errCode === 'USER_NOT_PRESENT_OR_BAD_CREDENTIALS' && (
        <WarningSection className={`${className}-warningsection`}>
          <WarningBox>
            <img src={warningIcon} />
            <span style={{ color: '#d80027', lineHeight: '16px', fontWeight: '700' }}>Warning</span>
          </WarningBox>
          <WarningContent>
            It looks like you are having problems signing In.{' '}
            {pendingAttempts >= 1
              ? `You have ${pendingAttempts + 1} attempts remaining before your account is locked.`
              : 'This is your last attempt before your account is locked!'}{' '}
            Would you like to <Link to="/auth/reset-password">change your password</Link>?
          </WarningContent>
        </WarningSection>
      )
    );
  };

  const emailUnknownWarningBox = () => {
    return (
      errCode === 'ENTITY_NOT_FOUND' && (
        <>
          <WarningBox>
            <img src={warningIcon} />
            <span style={{ color: '#d80027', lineHeight: '16px', fontWeight: '700' }}>Warning</span>
          </WarningBox>
          <WarningContent>
            Email not found! Please check this is the email you used to register. <br />
            If the error persists{' '}
            <a href="#contact" style={{ textDecoration: 'underline', color: '#727272' }}>
              please call support for assistance.
            </a>
          </WarningContent>
        </>
      )
    );
  };

  return (
    <>
      {className === 'admin' && (
        <div className="logo-wrapper">
          <img className="logo" src={TenantConfig.headerLogo} alt="logo" />
        </div>
      )}
      <div
        className={`${className === 'admin' ? 'admin-form topoffset-signin' : className} section`}
      >
        <div className={`${className}-header`}>
          <h1>
            {className === 'admin' ? (
              'Merchant Partner Login'
            ) : width >= 412 ? (
              'Welcome! Sign In!'
            ) : (
              <>
                Welcome! <br /> Sign In!
              </>
            )}
          </h1>
        </div>
        {className === 'admin' && (
          <div className="admin-form__description-wrapper">
            <div className="admin-form__description">Please enter your details</div>
            <div className="admin-divider" />
          </div>
        )}
        <div className={`${className}-main`}>
          <Form
            onSubmit={onSubmitInternal}
            initialValues={{
              phone: '+1-___-___-___',
            }}
            render={({ handleSubmit, form, submitting, pristine, valid, submitError }) => (
              <form onSubmit={handleSubmit} className="form">
                {formOptions.fields.map((f) => {
                  return <FormInput {...f} key={`form_${f.name}`} resetErrors={resetErrors} />;
                })}
                {errMsg?.length > 0 && (
                  <WarningBox>
                    <img src={errorIcon} />
                    <span>{errMsg}</span>
                    {errCode === 'USER_IS_DISABLED' && (
                      <a href="#contact">Please call support for assistance.</a>
                    )}
                  </WarningBox>
                )}
                {className === 'admin' ? (
                  <div className="sidetext-wrapper">
                    <p className="text-left no-margin description">
                      <Link to={`/auth/reset-password?app=/${className}`}>
                        {className === 'admin' ? 'Forgot password?' : 'Forgot your password?'}
                      </Link>
                    </p>
                    <button
                      className="btn primary-btn"
                      type="submit"
                      disabled={submitting || pristine || !valid}
                    >
                      Sign In
                    </button>
                    {submitError && submitError == 'USER_IS_DISABLED' && (
                      <div className={`${className}-error error-text`}>
                        <p>The account is locked out due to too many failed login attempts.</p>
                        <p>
                          Please{' '}
                          <a href="#contact" onClick={scrollToBottom}>
                            contact support
                          </a>
                          .{' '}
                        </p>
                      </div>
                    )}
                    {submitError && submitError == 'USER_NOT_PRESENT_OR_BAD_CREDENTIALS' && (
                      <div className={`${className}-error error-text`}>
                        <p>The account is locked out due to too many failed login attempts.</p>
                        <p>
                          Please{' '}
                          <a href="#contact" onClick={scrollToBottom}>
                            contact support
                          </a>
                          .{' '}
                        </p>
                      </div>
                    )}
                    {submitError &&
                      submitError !== 'USER_IS_DISABLED' &&
                      submitError !== 'USER_NOT_PRESENT_OR_BAD_CREDENTIALS' && (
                        <>
                          {className === 'admin' ? (
                            <div className="error">
                              <p className="error-text">{submitError}</p>
                            </div>
                          ) : (
                            <p className="error-text">{submitError}</p>
                          )}
                        </>
                      )}
                  </div>
                ) : (
                  <>
                    {emailUnknownWarningBox()}
                    {badCredentialsWarningBox()}
                    <p className="text-right no-margin">
                      <Link to="/auth/reset-password">Forgot your password?</Link>
                    </p>
                    <button
                      className="btn primary-btn"
                      type="submit"
                      disabled={submitting || pristine}
                    >
                      Sign In
                    </button>
                    {submitError &&
                      (submitError == 'USER_IS_DISABLED' ? (
                        <div className={`${className}-error error-text`}>
                          <p>The account is locked out due to too many failed login attempts.</p>
                          <p>
                            Please{' '}
                            <a href="#contact" onClick={scrollToBottom}>
                              contact support
                            </a>
                            .{' '}
                          </p>
                        </div>
                      ) : (
                        <>
                          {className === 'admin' ? (
                            <div className="error-text">
                              <p>{submitError}</p>
                            </div>
                          ) : (
                            <p className="error-text">{submitError}</p>
                          )}
                        </>
                      ))}
                  </>
                )}
              </form>
            )}
          />
        </div>
        <p className={`${className}-bottom subtitle`}>
          <small>
            <Link to={`/auth/register${location.search}`}>Don't have an account? Sign up!</Link>
          </small>
        </p>
      </div>

      {TenantConfig?.auth.renderBottomImg && className !== 'admin' && (
        <img
          className="phone-verification-bg"
          src={verificationBGImage}
          alt="Verification Background Image"
        />
      )}
      {className === 'admin' && <MerchantPortalFooter />}
    </>
  );
};

export default SignIn;
